import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectNameText from '../../components/ProjectNameText';
import TabbedSection from '../../components/TabbedSection';
import { submittalsAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../utils/notify';
import { formatDateIN, formatDateTimeIN } from '../../utils/dateFormat';
import { openSubmittalTransmittalPrint } from '../../utils/submittalTransmittalPrint';
import SubmittalStatusBadge, { formatSubmittalStatus } from './SubmittalStatusBadge';
import {
  CONSULTANT_REVIEW_BLOCKED_MSG,
  getAvailableWorkflowActions,
  isSubmitToClientBlocked,
} from './submittalPermissions';


const formatDynamicFieldValue = (fieldType, value) => {
  if (value == null || value === '') return '—';
  const type = String(fieldType || 'TEXT').toUpperCase();
  if (type === 'BOOLEAN') return value ? 'Yes' : 'No';
  if (type === 'DATE') return formatDateIN(value);
  if (type === 'NUMBER') return String(value);
  if (type === 'MULTI_SELECT' && Array.isArray(value)) return value.join(', ') || '—';
  if (type === 'CHECKLIST' && Array.isArray(value)) {
    const checked = value.filter((item) => item?.checked);
    if (!checked.length) return '—';
    return checked.map((item) => item.label || 'Item').join('; ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ACTION_METHODS = {
  'submit-internal-review': submittalsAPI.submitInternalReview,
  'submit-to-consultant': submittalsAPI.submitToConsultant,
  'recommend-to-client': submittalsAPI.recommendToClient,
  'submit-to-client': submittalsAPI.submitToClient,
  comment: submittalsAPI.comment,
  approve: submittalsAPI.approve,
  'approve-with-comments': submittalsAPI.approveWithComments,
  'return-for-revision': submittalsAPI.returnForRevision,
  reject: submittalsAPI.reject,
  resubmit: submittalsAPI.resubmit,
  close: submittalsAPI.close,
  cancel: submittalsAPI.cancel,
  reassign: submittalsAPI.reassign,
  'extend-due-date': submittalsAPI.extendDueDate,
};

const APPROVED_PACKAGE_STATUSES = new Set(['APPROVED', 'APPROVED_WITH_COMMENTS', 'CLOSED']);

const SubmittalDetail = ({ submittalId, onBack, onViewThread }) => {
  const [submittal, setSubmittal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [templateFields, setTemplateFields] = useState([]);

  const loadDetail = useCallback(async () => {
    if (!submittalId) return;
    setLoading(true);
    try {
      const resp = await submittalsAPI.getById(submittalId);
      setSubmittal(resp?.data || null);
    } catch (err) {
      console.warn('Submittal detail load failed:', err?.message || err);
      setSubmittal(null);
    } finally {
      setLoading(false);
    }
  }, [submittalId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  useEffect(() => {
    if (!submittal?.template_id) {
      setTemplateFields([]);
      return;
    }
    submittalsAPI.getTemplateFields(submittal.template_id)
      .then((resp) => setTemplateFields(safeArray(resp?.data?.data)))
      .catch(() => setTemplateFields([]));
  }, [submittal?.template_id]);


  const runWorkflow = async (action) => {
    const fn = ACTION_METHODS[action.key];
    if (!fn) return;
    let payload = {};
    if (['comment', 'return-for-revision', 'reject', 'approve-with-comments'].includes(action.key)) {
      const comments = window.prompt(`${action.label} — enter comments (optional):`) ?? '';
      payload = { comments };
    }
    if (action.key === 'extend-due-date') {
      const due = window.prompt('New due date (YYYY-MM-DD):');
      if (!due) return;
      payload = { due_date: due };
    }
    if (action.key === 'resubmit') {
      const target = window.prompt('Target party (CONSULTANT or CLIENT):', 'CONSULTANT');
      if (!target) return;
      payload = { target_party: target.toUpperCase() };
    }
    if (!(await confirmDialog(`Proceed with "${action.label}"?`, action.label))) return;
    setActionBusy(true);
    try {
      await fn(submittalId, payload);
      notifySuccess(`${action.label} completed.`);
      await loadDetail();
    } catch (err) {
      notifyError(err?.response?.data?.message || `Unable to ${action.label.toLowerCase()}.`);
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !submittal) return;
    const revision = safeArray(submittal.revisions).find((r) => r.revision_no === submittal.current_revision_no)
      || safeArray(submittal.revisions)[submittal.revisions?.length - 1];
    if (!revision?.id) {
      notifyError('No active revision found for upload.');
      return;
    }
    setUploadBusy(true);
    try {
      await submittalsAPI.uploadAttachment(submittalId, revision.id, file);
      notifySuccess('Attachment uploaded.');
      await loadDetail();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploadBusy(false);
      e.target.value = '';
    }
  };

  const downloadAttachment = (att) => {
    const url = submittalsAPI.getDownloadAttachmentUrl(submittalId, att.revision_id, att.id, true);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePrintTransmittal = async () => {
    setPrintBusy(true);
    try {
      const resp = await submittalsAPI.getTransmittal(submittalId);
      openSubmittalTransmittalPrint(resp?.data || resp);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to load transmittal for printing.');
    } finally {
      setPrintBusy(false);
    }
  };

  const handleDownloadApprovedPackage = async () => {
    setDownloadBusy(true);
    try {
      const resp = await submittalsAPI.downloadApprovedPackage(submittalId);
      const blob = resp?.data;
      if (!blob) throw new Error('Empty download response');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${(submittal?.submittal_no || `submittal-${submittalId}`).replace(/[\\/:*?"<>|]+/g, '_')}-approved-package.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      notifySuccess('Approved package downloaded.');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to download approved package.');
    } finally {
      setDownloadBusy(false);
    }
  };

  const revisions = safeArray(submittal?.revisions);
  const revisionLabelById = useMemo(() => {
    const map = {};
    revisions.forEach((rev) => {
      map[rev.id] = rev.revision_label || `R${rev.revision_no}`;
    });
    return map;
  }, [revisions]);


  const submissionDetails = useMemo(() => {
    if (!submittal) return [];
    const currentRevision = revisions.find((r) => r.revision_no === submittal.current_revision_no)
      || revisions[revisions.length - 1];
    const values = currentRevision?.dynamic_field_values && typeof currentRevision.dynamic_field_values === 'object'
      ? currentRevision.dynamic_field_values
      : {};
    const rows = [];
    const usedKeys = new Set();

    templateFields.forEach((field) => {
      usedKeys.add(field.field_key);
      rows.push({
        key: field.field_key,
        label: field.label,
        value: formatDynamicFieldValue(field.field_type, values[field.field_key]),
      });
    });

    Object.entries(values).forEach(([key, value]) => {
      if (usedKeys.has(key)) return;
      rows.push({ key, label: key.replace(/_/g, ' '), value: formatDynamicFieldValue('TEXT', value) });
    });

    return rows;
  }, [submittal, revisions, templateFields]);

  const unifiedTimeline = useMemo(() => {
    if (!submittal) return [];
    const actionItems = safeArray(submittal.actions).map((item) => ({
      id: `action-${item.id}`,
      date: item.created_at,
      action: String(item.action || '').replace(/_/g, ' '),
      by: item.action_by?.name || '—',
      fromStatus: item.from_status,
      toStatus: item.to_status,
      comments: item.comments,
      revisionRef: item.revision_id ? (revisionLabelById[item.revision_id] || `Rev #${item.revision_id}`) : null,
    }));
    const historyItems = safeArray(submittal.status_history).map((item) => ({
      id: `status-${item.id}`,
      date: item.created_at,
      action: 'Status Change',
      by: item.changed_by?.name || '—',
      fromStatus: item.from_status,
      toStatus: item.to_status,
      comments: item.remarks,
      revisionRef: item.revision_id ? (revisionLabelById[item.revision_id] || `Rev #${item.revision_id}`) : null,
    }));
    return [...actionItems, ...historyItems].sort(
      (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime(),
    );
  }, [submittal, revisionLabelById]);

  if (loading) return <div className="loading-state"><p>Loading submittal...</p></div>;
  if (!submittal) {
    return (
      <div className="empty-state">
        <h3>Submittal Not Found</h3>
        <button type="button" className="btn-primary" onClick={onBack}>Back to Register</button>
      </div>
    );
  }

  const actions = getAvailableWorkflowActions(submittal);
  const consultantReviewBlocked = isSubmitToClientBlocked(submittal);
  const showApprovedPackage = APPROVED_PACKAGE_STATUSES.has(String(submittal.status || '').toUpperCase());
  const attachments = safeArray(submittal.attachments);

  return (
    <div>
      <button type="button" className="btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>Back to Register</button>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px' }}>
              <ProjectNameText as="span">{submittal.project?.name || '—'}</ProjectNameText>
              {' · '}{submittal.category?.name || '—'}
            </p>
            <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>{submittal.subject}</h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
              {submittal.submittal_no || submittal.base_number || `#${submittal.id}`}
              {' · '}{submittal.current_revision_label || 'R0'}
              {' · Due '}{formatDateIN(submittal.due_date)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <SubmittalStatusBadge status={submittal.status} isOverdue={submittal.is_overdue} />
          </div>
        </div>

        {consultantReviewBlocked && (
          <p style={{ margin: '16px 0 0', padding: '10px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontSize: 13 }}>
            {CONSULTANT_REVIEW_BLOCKED_MSG}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              className={action.key === 'reject' || action.key === 'cancel' ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}
              disabled={actionBusy}
              onClick={() => runWorkflow(action)}
            >
              {action.label}
            </button>
          ))}
          <button type="button" className="btn-secondary btn-sm" disabled={printBusy} onClick={handlePrintTransmittal}>
            {printBusy ? 'Loading...' : 'Print Transmittal'}
          </button>
          {showApprovedPackage && (
            <button type="button" className="btn-secondary btn-sm" disabled={downloadBusy} onClick={handleDownloadApprovedPackage}>
              {downloadBusy ? 'Downloading...' : 'Download Approved Package'}
            </button>
          )}
          <label className="btn-secondary btn-sm" style={{ cursor: uploadBusy ? 'wait' : 'pointer' }}>
            {uploadBusy ? 'Uploading...' : 'Upload Attachment'}
            <input type="file" style={{ display: 'none' }} onChange={handleUpload} disabled={uploadBusy} />
          </label>
        </div>
      </div>

      <TabbedSection
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={[
          {
            key: 'timeline',
            label: 'Workflow & Audit Timeline',
            content: (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>By</th>
                      <th>Revision</th>
                      <th>From → To</th>
                      <th>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unifiedTimeline.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No workflow or audit events yet</td></tr>
                    ) : unifiedTimeline.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDateTimeIN(item.date)}</td>
                        <td>{item.action}</td>
                        <td>{item.by}</td>
                        <td>{item.revisionRef || '—'}</td>
                        <td>{formatSubmittalStatus(item.fromStatus)} → {formatSubmittalStatus(item.toStatus)}</td>
                        <td>{item.comments || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            key: 'submission',
            label: 'Submission Details',
            content: (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissionDetails.length === 0 ? (
                      <tr><td colSpan={2} style={{ textAlign: 'center', color: '#6b7280' }}>No submission details recorded</td></tr>
                    ) : submissionDetails.map((row) => (
                      <tr key={row.key}>
                        <td style={{ fontWeight: 500, width: '35%' }}>{row.label}</td>
                        <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            key: 'revisions',
            label: 'Revisions',
            content: (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Revision</th>
                      <th>Status</th>
                      <th>Submitted By</th>
                      <th>Submitted At</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisions.map((rev) => (
                      <tr key={rev.id}>
                        <td>{rev.revision_label}</td>
                        <td>{formatSubmittalStatus(rev.status)}</td>
                        <td>{rev.submitted_by?.name || '—'}</td>
                        <td>{formatDateTimeIN(rev.submitted_at)}</td>
                        <td>{rev.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            key: 'attachments',
            label: 'Attachments',
            content: (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Revision</th>
                      <th>Uploaded By</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>No attachments</td></tr>
                    ) : attachments.map((att) => (
                      <tr key={att.id}>
                        <td>{att.original_name || att.file_name}</td>
                        <td>R{att.revision_id}</td>
                        <td>{att.uploaded_by?.name || '—'}</td>
                        <td>{formatDateIN(att.created_at)}</td>
                        <td>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(att)}>Download</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            key: 'linked',
            label: 'Linked Correspondence',
            content: submittal.linked_correspondence_chain ? (
              <LinkedCorrespondenceCard
                linkedCorrespondenceId={submittal.linked_correspondence_chain.id}
                onViewThread={onViewThread}
              />
            ) : (
              <div className="empty-state">
                <h3>No Linked Correspondence</h3>
                <p>This submittal is not linked to a correspondence subject.</p>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SubmittalDetail;
